import * as React from 'react';
import axios from 'axios';
import { Alert, Button, Checkbox, Modal, notification, Table, Upload } from 'antd';
import { DownloadOutlined, FileExcelOutlined, InboxOutlined } from '@ant-design/icons';

import { url } from '../../config/config';

interface IPreviewItem {
  row: number;
  customerId: string;
  email: string;
  name: string | null;
  oldDiscount: number;
  newDiscount: number;
  changed: boolean;
}

interface IPreview {
  hasHeader: boolean;
  skipFirstRow: boolean;
  header: string[] | null;
  items: IPreviewItem[];
  unmatched: Array<{ row: number; email: string }>;
  invalid: Array<{ row: number; email: string }>;
}

interface ICustomerDiscountImportProps {
  onClose: () => void;
  onImported: () => void;
}

interface ICustomerDiscountImportState {
  file?: File;
  preview?: IPreview;
  skipFirstRow?: boolean;
  loading: boolean;
  applying: boolean;
  changesOnly: boolean;
}

const colors = {
  muted: '#8c8c8c',
  up: { background: '#f6ffed', color: '#389e0d', border: '1px solid #d9f7be' },
  down: { background: '#fff1f0', color: '#cf1322', border: '1px solid #ffccc7' },
  same: { background: '#fafafa', color: '#bfbfbf', border: '1px solid #f0f0f0' },
};

const DiscountDiff = ({ oldValue, newValue }: { oldValue: number; newValue: number }) => {
  const diff = newValue - oldValue;
  const pill = diff === 0 ? colors.same : diff > 0 ? colors.up : colors.down;

  return (
    <div style={{ whiteSpace: 'nowrap', lineHeight: '18px' }}>
      <div>
        <span style={{ color: colors.muted }}>{oldValue} %</span>
        <span style={{ color: colors.muted, margin: '0 4px' }}>→</span>
        <strong>{newValue} %</strong>
      </div>
      <span style={{ ...pill, display: 'inline-block', fontSize: 11, lineHeight: '16px', padding: '0 6px', borderRadius: 8 }}>
        {diff === 0 ? 'no change' : `${diff > 0 ? '+' : ''}${diff} pp`}
      </span>
    </div>
  );
};

export default class CustomerDiscountImport extends React.Component<ICustomerDiscountImportProps, ICustomerDiscountImportState> {

  constructor(props: ICustomerDiscountImportProps) {
    super(props);

    this.state = {
      loading: false,
      applying: false,
      changesOnly: false,
    };
  }

  public render() {
    const { file, preview, loading, applying } = this.state;
    const changedCount = preview ? preview.items.filter(i => i.changed).length : 0;

    return (
      <Modal
        visible={true}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }}
        title="Import customer discounts"
        bodyStyle={{ padding: '12px 24px' }}
        onCancel={() => this.props.onClose()}
        footer={
          <div style={{ display: 'flex' }}>
            <Button icon={<DownloadOutlined />} disabled={!preview || preview.items.length === 0 || loading} onClick={() => this.downloadBackup()}>
              Download backup
            </Button>
            <span style={{ marginLeft: 'auto' }}>
              <Button onClick={() => this.props.onClose()}>Cancel</Button>
              <Button type="primary" disabled={changedCount === 0 || loading} loading={applying} onClick={() => this.confirm()}>
                {changedCount > 0 ? `Import ${changedCount} discounts` : 'Import'}
              </Button>
            </span>
          </div>
        }
      >
        {file ? this.renderToolbar(file, preview) : (
          <Upload.Dragger {...this.uploadProps()}>
            <div style={{ padding: '4px 0' }}>
              <InboxOutlined style={{ fontSize: 28, color: '#1890ff' }} />
              <div>Click or drag an .xlsx or .csv file here</div>
              <div style={{ color: colors.muted, fontSize: 12 }}>First column = customer email, second column = discount in %</div>
            </div>
          </Upload.Dragger>
        )}
        {preview && this.renderMessages(preview)}
        {(preview || loading) && this.renderItems(preview)}
      </Modal>
    );
  }

  private uploadProps() {
    return {
      accept: '.xlsx,.xls,.csv,.tsv,.txt',
      multiple: false,
      showUploadList: false,
      beforeUpload: (file: File) => {
        this.setState({ file, preview: undefined, skipFirstRow: undefined }, () => this.requestPreview());
        return false;
      },
    };
  }

  private renderToolbar(file: File, preview?: IPreview) {
    const changedCount = preview ? preview.items.filter(i => i.changed).length : 0;

    return (
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
        <Upload {...this.uploadProps()}>
          <Button size="small" icon={<FileExcelOutlined />} title="Choose another file">{file.name}</Button>
        </Upload>
        <Checkbox
          checked={this.state.skipFirstRow}
          disabled={!preview}
          onChange={e => this.setState({ skipFirstRow: e.target.checked }, () => this.requestPreview())}
        >
          Ignore first row
          {preview && preview.hasHeader &&
            <span style={{ color: colors.muted, fontSize: 12 }}> (header detected{preview.header ? `: ${preview.header.join(', ')}` : ''})</span>}
        </Checkbox>
        <Checkbox
          checked={this.state.changesOnly}
          disabled={!preview}
          onChange={e => this.setState({ changesOnly: e.target.checked })}
        >
          Show changes only
        </Checkbox>
        {preview &&
          <span style={{ marginLeft: 'auto', color: colors.muted, fontSize: 12 }}>
            {preview.items.length} matched · <strong style={{ color: 'inherit' }}>{changedCount} changed</strong>
            {preview.unmatched.length > 0 && ` · ${preview.unmatched.length} not found`}
          </span>}
      </div>
    );
  }

  private renderMessages(preview: IPreview) {
    const list = (rows: Array<{ row: number; email: string }>) => rows.map(u => `${u.email} (row ${u.row})`).join(', ');
    const style = { marginBottom: 8, padding: '2px 12px', fontSize: 12 };

    return (
      <React.Fragment>
        {preview.unmatched.length > 0 &&
          <Alert type="warning" showIcon={true} style={style} message={`Active customers not found: ${list(preview.unmatched)}`} />}
        {preview.invalid.length > 0 &&
          <Alert
            type="warning"
            showIcon={true}
            style={style}
            message={`Rows with an invalid discount (whole number 0–100 expected): ${list(preview.invalid)}`}
          />}
      </React.Fragment>
    );
  }

  private renderItems(preview?: IPreview) {
    return (
      <Table
        size="small"
        loading={this.state.loading}
        rowKey="customerId"
        dataSource={preview ? preview.items.filter(i => i.changed || !this.state.changesOnly) : []}
        pagination={false}
        scroll={{ y: 'calc(100vh - 260px)' }}
        onRow={record => ({ style: record.changed ? {} : { opacity: 0.45 } })}
      >
        <Table.Column
          title="Customer"
          key="customer"
          ellipsis={true}
          render={(u, record: IPreviewItem) => (
            <div style={{ lineHeight: '18px' }}>
              <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {record.email}
              </strong>
              <div style={{ color: colors.muted, fontSize: 12 }}>
                {record.name}<span style={{ fontSize: 11, marginLeft: record.name ? 8 : 0 }}>row {record.row}</span>
              </div>
            </div>
          )}
        />
        <Table.Column
          title="Discount"
          key="discount"
          align="right"
          width={170}
          render={(u, record: IPreviewItem) => <DiscountDiff oldValue={record.oldDiscount} newValue={record.newDiscount} />}
        />
      </Table>
    );
  }

  private send(action: 'preview' | 'apply') {
    const data = new FormData();
    data.append('file', this.state.file as File);
    data.append('options', JSON.stringify({ skipFirstRow: this.state.skipFirstRow }));

    return axios.post(`${url}/customers/importDiscounts/${action}`, data, {
      headers: { Authorization: localStorage.getItem('token') },
    });
  }

  private requestPreview() {
    if (!this.state.file) {
      return;
    }

    this.setState({ loading: true });
    this.send('preview')
      .then(response => {
        const preview: IPreview = response.data;
        this.setState({ preview, loading: false, skipFirstRow: preview.skipFirstRow });
      })
      .catch(error => {
        this.setState({ loading: false, preview: undefined });
        this.showError(error);
      });
  }

  // current discounts in the import format, so they can be imported back
  private downloadBackup() {
    const { preview } = this.state;
    if (!preview) {
      return;
    }

    const emails: string[] = [];
    const lines = ['Email;Company Discount'];
    for (const item of preview.items) {
      if (emails.indexOf(item.email.toLowerCase()) >= 0) {
        continue;
      }
      emails.push(item.email.toLowerCase());
      lines.push(`${item.email};${item.oldDiscount}`);
    }

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    link.download = `discounts-backup-${new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private confirm() {
    const { preview } = this.state;
    if (!preview) {
      return;
    }

    const changed = preview.items.filter(i => i.changed);
    const increased = changed.filter(i => i.newDiscount > i.oldDiscount).length;

    Modal.confirm({
      title: 'Do you really want to import discounts?',
      content: (
        <span>
          Discounts of <strong>{changed.length}</strong> customers will be overwritten
          ({increased} increased, {changed.length - increased} decreased). This cannot be undone.
        </span>
      ),
      okText: 'Yes, import discounts',
      okButtonProps: { danger: true },
      onOk: () => this.apply(),
    });
  }

  private apply() {
    this.setState({ applying: true });

    return this.send('apply')
      .then(response => {
        this.setState({ applying: false });
        notification.success({
          message: 'Discounts imported',
          description: `Discounts of ${response.data.updated} customers have been updated.`,
        });
        this.props.onImported();
      })
      .catch(error => {
        this.setState({ applying: false });
        this.showError(error);
      });
  }

  private showError(error: any) {
    notification.error({
      message: 'Discount import failed',
      description: (error.response && error.response.data && error.response.data.message) || error.message,
    });
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Customers/CustomerDiscountImport.tsx